import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ArrowCanvas from "./arrow-canvas";
import getScrollableParent from "./scrollable-parent";

const NODES_VERTICAL_SPACE = 50;
const NODES_HORIZONTAL_SPACE = 50;

function transformGraphData(graphData, onResourceClick) {
  const nodes = Object.values(graphData).map((contract) => {
    const content = (
      <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
        <strong style={{ fontSize: '1.2em', color: '#333' }}>{contract.label}</strong>
        <br />
        {contract.functions && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333' }}>Functions:</strong>
            {Object.entries(contract.functions).map(([visibility, funcs]) => (
              funcs.length > 0 && (
                <div key={`func-${visibility}`} style={{ marginLeft: '10px' }}>
                  <strong style={{ color: getColorForVisibility(visibility) }}>{capitalize(visibility)}:</strong>
                  <ul>
                    {funcs.map((func, index) => {
                      var baseTextDecoration=func.overLoadedBy ? 'line-through' : 'none'
                      return (
                      <li
                        key={`func-${visibility}-${index}`}
                        style={{
                          color: getColorForVisibility(visibility),
                          cursor: 'pointer',
                          textDecoration: baseTextDecoration,
                          fontWeight: func.overloading && func.overloading.length > 0 ? 'bold' : 'normal'
                        }}
                        onClick={() => onResourceClick(func)}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = baseTextDecoration + ' underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = baseTextDecoration}
                      >
                        {func.name} ({func.stateMutability})
                      </li>
                    )})}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}
        {contract.modifiers?.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333' }}>Modifiers:</strong>
            <ul>
              {contract.modifiers.map((modifier, index) => (
                <li
                  key={`modifier-${index}`}
                  style={{ color: '#ffc107', cursor: 'pointer', textDecoration: 'none' }}
                  onClick={() => onResourceClick(modifier)}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {modifier.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {contract.events?.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333' }}>Events:</strong>
            <ul>
              {contract.events.map((event, index) => (
                <li
                  key={`event-${index}`}
                  style={{
                    color: '#17a2b8',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontWeight: event.overloading && event.overloading.length > 0 ? 'bold' : 'normal'
                  }}
                  onClick={() => onResourceClick(event)}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {event.overLoadedBy? `-- ${event.name} --` : event.name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {contract.storage && (
          <div style={{ marginTop: '10px' }}>
            <strong style={{ color: '#333' }}>Storage:</strong>
            {Object.entries(contract.storage).map(([visibility, stores]) => (
              stores.length > 0 && (
                <div key={`storage-${visibility}`} style={{ marginLeft: '10px' }}>
                  <strong style={{ color: getColorForVisibility(visibility) }}>{capitalize(visibility)}:</strong>
                  <ul>
                    {stores.map((store, index) => (
                      <li
                        key={`storage-${visibility}-${index}`}
                        style={{ color: getColorForVisibility(visibility), cursor: 'pointer', textDecoration: 'none' }}
                        onClick={() => onResourceClick(store)}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        {store.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    );

    return {
      id: contract.id,
      content,
      label: contract.label,
      childs: contract.dependencies?.map((dep) => dep.name) || [],
    };
  });

  // Identify root nodes (contracts with no dependencies)
  const rootNodes = nodes.filter(
    (node) => !Object.values(graphData).some((contract) =>
      contract.dependencies.some((dep) => dep.name === node.id)
    )
  );

  // Set the root property for each root node
  rootNodes.forEach((node) => (node.root = true));

  return nodes;
}

function getColorForVisibility(visibility) {
  switch (visibility) {
    case 'public':
      return '#007bff';
    case 'private':
      return '#dc3545';
    case 'internal':
      return '#6c757d';
    case 'external':
      return '#28a745';
    default:
      return '#333';
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function Graph({ scState, onResourceClick }) {
  const nodes = transformGraphData(scState.graphData, onResourceClick);

  const [openedChilds, set_openedChilds] = useState({});

  const expandAll = () => {
    const allOpen = {};
    const openAllNodes = (node, path) => {
      allOpen[path] = true;
      node.childs.forEach(childId => {
        const childNode = nodes.find(n => n.id === childId);
        if (childNode) {
          openAllNodes(childNode, `${path}/${childNode.id}`);
        }
      });
    };
    nodes.forEach(node => {
      if (node.root) {
        openAllNodes(node, `/${node.id}`);
      }
    });
    set_openedChilds(allOpen);
  };

  const collapseAll = () => {
    set_openedChilds({});
  };

  return (
    <div style={{ position: "relative", padding: "20px" }}>
      <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
        <span
          style={{ cursor: "pointer", marginRight: 10 }}
          title="Expand All"
          onClick={expandAll}
        >
          🔍+
        </span>
        <span
          style={{ cursor: "pointer" }}
          title="Collapse All"
          onClick={collapseAll}
        >
          🔍-
        </span>
      </div>
      <div style={{ position: "relative" }}>
        {nodes.filter((node) => node.root).map((rootNode) => (
          <Node
            key={rootNode.id}
            top={0}
            set_openedChilds={set_openedChilds}
            left={0}
            openedChilds={openedChilds}
            node={rootNode}
            nodes={nodes}
            path={`/${rootNode.id}`}
            onResourceClick={onResourceClick}
          />
        ))}
      </div>
    </div>
  );
}

function Node({
  node,
  nodes,
  updateSize = null,
  top,
  left,
  openedChilds,
  set_openedChilds,
  path,
  onResourceClick,
}) {
  if (!node.childs) {
    node.childs = [];
  }

  let childNodes = nodes.filter((x) => node.childs.includes(x.id));
  const hasChilds = childNodes.length > 0;

  if (!openedChilds[path]) {
    childNodes = [];
  }

  const [childNodeSizes, set_childNodeSizes] = useState({});
  const [lastSizeCache, set_lastSizeCache] = useState({ width: 0, height: 0 });

  function childSizeChanged(nodeId, size) {
    childNodeSizes[nodeId] = size;
    set_childNodeSizes({
      ...childNodeSizes,
    });
  }

  const nodeElement = useRef(null);
  const contentElement = useRef(null);

  const [childNodesPositions, set_childNodePosition] = useState({});
  const [contentTop, set_contentTop] = useState(0);
  const [isReady, set_isReady] = useState(false);

  useEffect(() => {
    if (nodeElement.current == null) return;

    const childSizesReady =
      Object.keys(childNodeSizes).length === childNodes.length;

    if (childSizesReady) {
      const totalChildHeight = childNodes.reduce(
        (prev, node) => prev + childNodeSizes[node.id].height,
        0
      );
      const meWidth = nodeElement.current.offsetWidth;
      const meHeight = Math.max(
        nodeElement.current.offsetHeight,
        totalChildHeight
      );

      const meContentHeight = contentElement.current.offsetHeight;
      nodeElement.current.setAttribute("meContentHeight", meContentHeight);
      nodeElement.current.setAttribute("meWidth", meWidth);
      nodeElement.current.setAttribute("totalChildHeight", totalChildHeight);
      nodeElement.current.setAttribute("childNodes", childNodes.length);
      nodeElement.current.setAttribute("IOpened", openedChilds[path]);

      let currentTop = 0;
      let newContentTop = 0;
      if (totalChildHeight > meContentHeight) {
        newContentTop = totalChildHeight / 2 - meContentHeight / 2;
      } else {
        newContentTop = 0;
      }
      set_contentTop((prev) => {
        if (node.root && prev - newContentTop !== 0) {
          const scrollElement = getScrollableParent(contentElement.current);
          const after = scrollElement.scrollTop - (prev - newContentTop);
          scrollElement.scrollTop = after;
        }
        return newContentTop;
      });

      const currentLeft = meWidth + NODES_HORIZONTAL_SPACE;
      const newChildNodesPositions = {};
      for (let i = 0; i < childNodes.length; i++) {
        const childNode = childNodes[i];

        newChildNodesPositions[childNode.id] = {
          top: currentTop,
          left: currentLeft,
        };

        currentTop += childNodeSizes[childNode.id].height + NODES_VERTICAL_SPACE;
      }

      if (
        JSON.stringify(newChildNodesPositions) !==
        JSON.stringify(childNodesPositions)
      ) {
        set_childNodePosition(newChildNodesPositions);
      }

      if (
        meWidth !== 0 &&
        meHeight !== 0 &&
        (meWidth !== lastSizeCache.width || meHeight !== lastSizeCache.height)
      ) {
        updateSize?.({ width: meWidth, height: meHeight });

        set_lastSizeCache({
          width: meWidth,
          height: meHeight,
        });
        set_isReady(true);
      }
    }
  });

  function toggle() {
    if (!openedChilds[path]) {
      openedChilds[path] = true;
    } else {
      Object.keys(openedChilds).forEach((x) => {
        if (x.startsWith(path)) {
          delete openedChilds[x];
        }
      });
    }
    set_openedChilds({
      ...openedChilds,
    });
  }

  useLayoutEffect(() => {
    if (node.root) {
      contentElement.current.scrollIntoView({ block: "center" });
    }
  }, []);

  return (
    <div
      ref={nodeElement}
      style={{
        position: "absolute",
        visibility: isReady ? "visible" : "hidden",
        top,
        left,
        display: "inline-block",
      }}
    >
      <div
        ref={contentElement}
        style={{
          whiteSpace: "nowrap",
          position: "relative",
          border: "1px solid #333",
          borderRadius: 5,
          background: "#ddd",
          padding: 10,
          top: contentTop,
          userSelect: "none",
        }}
      >
        {node.content}
      </div>
      {hasChilds && childNodes.length > 0 && (
        <svg
          onClick={toggle}
          style={{
            cursor: "pointer",
            position: "absolute",
            zIndex: 1,
            left: contentElement.current?.offsetWidth + 5 - 5,
            top: contentTop + contentElement.current?.offsetHeight / 2 - 8,
          }}
          width="18"
          height="18"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="18"
            cy="18"
            r="16"
            stroke="black"
            strokeWidth="4"
            fill="white"
          />
        </svg>
      )}
      {hasChilds && childNodes.length === 0 && (
        <svg
          onClick={toggle}
          style={{
            cursor: "pointer",
            position: "absolute",
            left: contentElement.current?.offsetWidth + 5,
            top: contentTop + contentElement.current?.offsetHeight / 2,
          }}
          width="44"
          height="18"
          viewBox="0 0 88 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_27_1097)">
            <rect width="88" height="36" fill="white" />
            <path
              d="M52 18L32 6.45299V29.547L52 18ZM0 20H34V16H0V20Z"
              fill="black"
            />
            <circle cx="70" cy="18" r="16" stroke="black" strokeWidth="4" />
          </g>
          <defs>
            <clipPath id="clip0_27_1097">
              <rect width="88" height="36" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
      {childNodes.map((childNode) => (
        <React.Fragment key={childNode.id}>
          <ArrowCanvas
            fromX={contentElement.current?.offsetWidth + 5}
            fromY={contentTop + contentElement.current?.offsetHeight / 2}
            toX={childNodesPositions[childNode.id]?.left - 5}
            toY={
              childNodesPositions[childNode.id]?.top +
              childNodeSizes[childNode.id]?.height / 2
            }
          />
          <Node
            path={path + "/" + childNode.id}
            set_openedChilds={set_openedChilds}
            openedChilds={openedChilds}
            top={childNodesPositions[childNode.id]?.top}
            left={childNodesPositions[childNode.id]?.left}
            node={childNode}
            nodes={nodes}
            updateSize={(size) => childSizeChanged(childNode.id, size)}
            onResourceClick={onResourceClick}
          />
        </React.Fragment>
      ))}
    </div>
  );
}
